import React from 'react';

/**
 * TimeSlot - Represents an available appointment time slot.
 *
 * Contains information about a specific time slot with a unique identifier
 * and the time string to display.
 */
export interface TimeSlot {
  id: string;
  time: string;
  timeRange?: string; // Optional time range format like "12pm - 1pm"
}

interface TimeSlotSelectorProps {
  timeSlots: TimeSlot[];
  onReserve: (time: string) => void;
  location?: string;
}

/**
 * TimeSlotSelector - A component for selecting appointment time slots.
 *
 * Displays available time slots in a list format and allows users to select
 * their preferred appointment time. The component highlights the selected time
 * and provides a "Reserve Time" button for each slot.
 *
 * @param timeSlots - Array of available time slots to display
 * @param onReserve - Callback function when a time slot is reserved
 *
 * @example
 * ```tsx
 * <TimeSlotSelector
 *   timeSlots={[
 *     { id: '1', time: '9:00 AM' },
 *     { id: '2', time: '10:00 AM' },
 *     { id: '3', time: '11:00 AM' }
 *   ]}
 *   onReserve={(time) => handleReservation(time)}
 * />
 * ```
 */
const TimeSlotSelector: React.FC<TimeSlotSelectorProps> = ({
  timeSlots,
  onReserve,
  location = 'Northgate',
}) => {
  if (timeSlots.length === 0) {
    return <div style={{ color: '#b5b5b5', margin: '18px 0' }} role="status" aria-live="polite">No available times</div>;
  }
  return (
    <div role="listbox" aria-label="Available appointment times" className="ml-18 mr-18">
      {timeSlots.map(slot => (
        <div
          key={slot.id}
          style={{
            border: '1px solid #b5c7d6',
            borderRadius: 10,
            padding: 18,
            background: '#fff',
            marginBottom: 12,
            cursor: 'pointer',
          }}
          onClick={() => onReserve(slot.time)}
          role="option"
          aria-selected="false"
          aria-label={`Reserve time slot for ${slot.time} at ${location}`}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onReserve(slot.time);
            }
          }}
        >
          <div className="mb-10" style={{ fontWeight: 600, color: '#185F93', fontSize: '1.08rem' }}>
            {slot.timeRange || slot.time} @ {location}
          </div>
          <button
            className="pill-btn-standard"
            onClick={e => { e.stopPropagation(); onReserve(slot.time); }}
            aria-label={`Reserve time slot for ${slot.time} at ${location}`}
          >
            Reserve Time
          </button>
        </div>
      ))}
    </div>
  );
};

export default TimeSlotSelector; 